/**
 * Blackjack-specific event types and interfaces
 * These events are specific to the blackjack game
 */

export namespace BlackjackEvents {
  /**
   * Event types for blackjack game
   */
  export enum Type {
    WELCOME = 'welcome',
    PLACE_BET = 'place_bet',
    BET_CONFIRMATION = 'bet_confirmation',
    HAND_ACTION = 'hand_action',
    HANDS_UPDATE = 'hands_update',
    SPLIT_HANDS_UPDATE = 'split_hands_update',
    GAME_OVER = 'game_over',
    SPLIT_GAME_OVER = 'split_game_over',
    BET_TIMER_EXPIRED = 'bet_timer_expired',
    INSURANCE_ACTION = 'insurance_action',
  }

  /**
   * Intermediate hand statuses used during gameplay
   */
  export type IntermediateStatus = 'playing' | 'standing';

  /**
   * Final game outcomes used in game_over events
   * - 'bust': Player went over 21
   * - 'blackjack': Player has natural 21 (first 2 cards)
   * - 'won': Player won (dealer bust or higher total)
   * - 'lost': Player lost (lower total, not bust)
   * - 'push': Tie game (same totals or both blackjack)
   */
  export type GameOutcome = 'bust' | 'blackjack' | 'push' | 'won' | 'lost';

  /**
   * Round outcome for split game_over (engine-provided; no inference in agent).
   */
  export type RoundOutcome = 'won' | 'lost' | 'push';

  /**
   * All possible player hand statuses (union of intermediate and final)
   */
  export type PlayerHandStatus = IntermediateStatus | GameOutcome;

  /**
   * Valid player moves in blackjack
   */
  export type PlayerMove = 'hit' | 'stand' | 'double' | 'split';

  /**
   * Reusable player hand structure (supports split and game_over breakdowns).
   * - During play: status is typically IntermediateStatus
   * - On game_over: status is GameOutcome, and win may be provided per hand
   *
   * See docs/features/blackjack-event-contract.md for full semantics.
   */
  export interface PlayerHand {
    cards: string[];
    total?: number;
    status?: PlayerHandStatus;
    /** Per-hand gross return (stake + payout) at game_over. Never negative. */
    win?: number;
    roundId?: string;
  }

  /**
   * Base interface for all blackjack events
   */
  interface BaseEvent {
    type: Type;
  }

  /**
   * Welcome event when player joins
   */
  export interface Welcome extends BaseEvent {
    type: Type.WELCOME;
    data?: {
      message?: string;
    };
  }

  /**
   * Place bet prompt event
   */
  export interface PlaceBet extends BaseEvent {
    type: Type.PLACE_BET;
    data?: {
      minBet?: number;
      maxBet?: number;
    };
  }

  /**
   * Bet confirmation event
   */
  export interface BetConfirmation extends BaseEvent {
    type: Type.BET_CONFIRMATION;
    data: {
      bet: number;
      roundId: string;
    };
  }

  /**
   * Hand action event
   */
  export interface HandAction extends BaseEvent {
    type: Type.HAND_ACTION;
    data: PlayerMove;
  }

  /**
   * Hands update event - single hand during gameplay
   */
  export type HandsUpdateData = {
    dealerHand: string[];
    dealerHandTotal?: number;
    playerHandCards: string[];
    playerHandStatus?: IntermediateStatus; // Only intermediate statuses during play
    playerHandTotal?: number;
    roundId?: string; // Optional for bet correlation
    dealerOpenCard?: string; // Optional convenience field
    insuranceAvailable?: boolean; // Triggers insurance popup when dealer shows Ace
    insuranceCost?: number; // Cost to take insurance (typically half bet)
  };

  export interface HandsUpdate extends BaseEvent {
    type: Type.HANDS_UPDATE;
    data: HandsUpdateData;
  }

  /**
   * Split hands update event - two hands during split gameplay
   */
  export type SplitHandsUpdateData = {
    dealerHand: string[];
    dealerHandTotal?: number;
    playerHands: PlayerHand[]; // Exactly 2 hands
    activeHand: 0 | 1;
    roundId?: string; // Optional for bet correlation
    dealerOpenCard?: string; // Optional convenience field
    insuranceAvailable?: boolean; // Triggers insurance popup when dealer shows Ace
    insuranceCost?: number; // Cost to take insurance (typically half bet)
  };

  export interface SplitHandsUpdate extends BaseEvent {
    type: Type.SPLIT_HANDS_UPDATE;
    data: SplitHandsUpdateData;
  }

  /**
   * Insurance action event - player takes or declines insurance.
   */
  export interface InsuranceAction extends BaseEvent {
    type: Type.INSURANCE_ACTION;
    data: {
      taken: boolean;
      amount?: number;
      roundId?: string;
    };
  }

  /**
   * Game over event - single hand final state.
   *
   * Contract: docs/features/blackjack-event-contract.md
   */
  export type GameOverData = {
    dealerHand: string[];
    dealerHandTotal: number;
    playerHandCards: string[];
    playerHandStatus: GameOutcome; // Only final outcomes at game end
    playerHandTotal: number;
    /**
     * Gross main return (stake + payout). Sum across split hands when split.
     * Excludes insurance. Never negative.
     *   - lose / bust: 0
     *   - push:       bet
     *   - win 1:1:    bet × 2
     *   - blackjack:  bet × 2.5
     */
    win: number;
    roundId: string; // Required for round correlation
    insuranceTaken?: boolean;
    /**
     * Gross insurance return (stake + payout). 0 if lost, betInsurance × 3 if
     * dealer BJ. Never negative. Required (by contract) when insuranceTaken=true,
     * though the agent falls back to a verdict-only render if missing.
     */
    insuranceWin?: number;
  };

  export interface GameOver extends BaseEvent {
    type: Type.GAME_OVER;
    data: GameOverData;
  }

  /**
   * Split game over event - two hands final state.
   *
   * Contract: docs/features/blackjack-event-contract.md
   */
  export type SplitGameOverData = {
    dealerHand: string[];
    dealerHandTotal: number;
    playerHands: PlayerHand[]; // Exactly 2 hands; per-hand win/status populated
    playerHandStatus: RoundOutcome; // Round outcome (won|lost|push)
    /**
     * Gross main return summed across both hands (stake + payout). Excludes
     * insurance. Never negative. Should equal sum of playerHands[i].win.
     */
    win: number;
    roundId: string; // Required for round correlation
    insuranceTaken?: boolean;
    /** Gross insurance return (stake + payout). See GameOverData.insuranceWin. */
    insuranceWin?: number;
  };

  export interface SplitGameOver extends BaseEvent {
    type: Type.SPLIT_GAME_OVER;
    data: SplitGameOverData;
  }

  /**
   * Bet timer expired event - triggers AI suspension
   */
  export interface BetTimerExpired extends BaseEvent {
    type: Type.BET_TIMER_EXPIRED;
    data?: undefined;
  }

  /**
   * Union type of all blackjack events
   */
  export type Event =
    | Welcome
    | PlaceBet
    | BetConfirmation
    | HandAction
    | HandsUpdate
    | SplitHandsUpdate
    | InsuranceAction
    | GameOver
    | SplitGameOver
    | BetTimerExpired;

  /**
   * Helper functions to create blackjack events
   */
  export function create<T extends Event>(type: T['type'], data?: T['data']): T {
    return {
      type,
      data,
    } as T;
  }

  export function createWelcome(message?: string): Welcome {
    return create(Type.WELCOME, message ? { message } : undefined);
  }

  export function createPlaceBet(minBet?: number, maxBet?: number): PlaceBet {
    return create(Type.PLACE_BET, minBet || maxBet ? { minBet, maxBet } : undefined);
  }

  export function createBetConfirmation(bet: number, roundId: string): BetConfirmation {
    return create(Type.BET_CONFIRMATION, { bet, roundId });
  }

  export function createHandAction(action: PlayerMove): HandAction {
    return create(Type.HAND_ACTION, action);
  }

  export function createInsuranceAction(
    taken: boolean,
    amount?: number,
    roundId?: string
  ): InsuranceAction {
    return create(Type.INSURANCE_ACTION, {
      taken,
      ...(amount !== undefined ? { amount } : {}),
      ...(roundId ? { roundId } : {}),
    });
  }

  export function createBetTimerExpired(): BetTimerExpired {
    return create(Type.BET_TIMER_EXPIRED, undefined);
  }

  /**
   * Type guards
   */
  export function isWelcome(event: Event): event is Welcome {
    return event.type === Type.WELCOME;
  }

  export function isBetConfirmation(event: Event): event is BetConfirmation {
    return event.type === Type.BET_CONFIRMATION;
  }

  export function isHandAction(event: Event): event is HandAction {
    return event.type === Type.HAND_ACTION;
  }

  export function isHandsUpdate(event: Event): event is HandsUpdate {
    return event.type === Type.HANDS_UPDATE;
  }

  export function isGameOver(event: Event): event is GameOver {
    return event.type === Type.GAME_OVER;
  }

  export function isInsuranceAction(event: Event): event is InsuranceAction {
    return event.type === Type.INSURANCE_ACTION;
  }

  export function isSplitHandsUpdate(event: Event): event is SplitHandsUpdate {
    return event.type === Type.SPLIT_HANDS_UPDATE;
  }

  export function isSplitGameOver(event: Event): event is SplitGameOver {
    return event.type === Type.SPLIT_GAME_OVER;
  }

  export function isBetTimerExpired(event: Event): event is BetTimerExpired {
    return event.type === Type.BET_TIMER_EXPIRED;
  }

  /** Canonical runtime-enumerable values used by validators. */
  export const PLAYER_MOVES: readonly PlayerMove[] = ['hit', 'stand', 'double', 'split'] as const;
  export const GAME_OUTCOMES: readonly GameOutcome[] = [
    'bust',
    'blackjack',
    'push',
    'won',
    'lost',
  ] as const;
  export const ROUND_OUTCOMES: readonly RoundOutcome[] = ['won', 'lost', 'push'] as const;
}
